import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum EightSleepPOLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const EightSleepPOAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type EightSleepPOFileAddressData = yup.InferType<typeof EightSleepPOAddressInfoSchema>

export const EightSleepPOFileDataSchema = yup.object().shape({
  issuer: EightSleepPOAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: EightSleepPOAddressInfoSchema,
  vendor: EightSleepPOAddressInfoSchema,
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  items2: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
})

export type EightSleepPOFileData = yup.InferType<typeof EightSleepPOFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<EightSleepPOFileData>

export const EightSleepPOFileMappings: RequiredFileData = {
  issuer: {
    attn: 'b21f937e-a17e-47d2-8e5b-d5c47472cd0d',
    companyName: 'a3d381bb-7df1-4d12-adc6-73813a067160',
    addressLine1: '73733283-6637-493d-b050-5f1852df7204',
    addressLine2: '210b9a80-d25c-4069-97aa-7e1e4ac435c5',
    city: '038066e8-d53d-40b3-a006-15ea4a875293',
    state: '0675559a-0964-4a63-b093-0101b248725e',
    zipCode: '95af6e2a-c276-4057-9545-9a5ef6235cce',
    country: 'ba5fae15-2483-44f2-9d72-825142747e2d',
    phoneNumber: 'c6363efe-0059-4a58-88d2-84954e43d7ba',
    emailAddress: '49731a00-d261-4bd1-8ed8-74e8a8b9f1eb',
    displayMultipleLines: '324b43d1-25e8-40d8-8697-09bc7801cf89',
  },
  orderNumber: 'a715a274-742c-4961-9b1f-c1fe778f6098',
  paymentTerms: 'c47a8801-ecf6-4dd4-a053-10d2c0a548f1',
  freightTerms: 'e6742b37-1c43-4a5c-83c5-8cc0b64257ea',
  dateIssued: 'b5705167-120d-450f-86dd-59d736c8ce34',
  dateReady: 'f9d9a213-1f61-4f2f-a522-fb9ba388621d',
  destination: {
    attn: 'd70f3cf1-af40-4567-b746-9adccec5e1f5',
    companyName: '9d405797-5907-4499-8f36-0f8f3d791a33',
    addressLine1: 'b0e43021-35bc-416d-b5f2-646bc862d851',
    addressLine2: '7615fd49-7ff9-4f2e-a7c7-86753354e3a8',
    city: '3aff56af-d87e-4c70-94c5-f9f781099d1b',
    state: '81a8baca-714f-402b-9392-533fd8c1ebed',
    zipCode: 'c3b746ea-a463-45c4-9262-c636e7342e0e',
    country: '3e2c095f-cd61-4a4e-9ebf-d45229ab00a9',
    phoneNumber: '36d4738f-8e73-481a-867b-942ee8c0cb25',
    emailAddress: '667015b1-935e-4aa3-a4a5-cbc9a091120d',
    displayMultipleLines: '88c5961d-3f79-402b-9540-5a041080d99f',
  },
  vendor: {
    attn: '38ced876-3ca7-483f-b993-b4a75ef59297',
    companyName: 'e57be91a-0744-4a86-9f78-ec6666798796',
    addressLine1: '3cd4ba1f-429f-4cd8-a446-836aaed2beb2',
    addressLine2: '3b77d2d7-3e9c-45cc-a9a3-b19659624e1f',
    city: '7b79488d-73cd-4d0f-93bf-ee5f8a507d27',
    state: '0e3b9258-ed2c-4783-84a7-f74a778252f6',
    zipCode: '35b1824f-2400-4ffe-99b3-dca9fa87e738',
    country: '03ceef0c-e2d4-4218-bf13-4225fb1daa1c',
    phoneNumber: '391e26e8-1880-409d-9817-bc7bb88ec41c',
    emailAddress: 'c52574e5-5e70-43cd-98f9-172844959120',
    displayMultipleLines: 'cf179a4a-ab0c-48c1-9a39-b2db03da2d69',
  },
  items: [
    {
      itemNumber: null,
      itemName: '0d905243-4906-4006-af08-cb7dc5f98205',
      name: '7963b580-ba91-484b-bb7f-251f4f738d6a',
      description: '89a48443-68e5-4a10-9ec2-014fcddbc668',
      itemQty: 'bf58728d-1569-4f59-91e2-8c44dbe674ab',
      unitPrice: '4281cb6f-7c63-4842-b556-b6b3d3d7201b',
      lineTotal: null,
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'd7ec1ce7-d9f9-4f77-b7d0-cd0af4207857',
      name: '259f138a-1330-46bb-91bf-8eca43543b3b',
      description: '3e41184f-6e99-4ec0-9744-dcac319d820d',
      itemQty: 'f088314d-f5a1-48e3-b09c-2c1b6bcea3c0',
      unitPrice: '306d4af1-f188-48fe-8c87-09725f42bb4c',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: 'a855d6fb-32c6-4251-86d6-c92f326d334e',
  agreement: 'f3bcdcfd-2628-48e7-9f23-4cae84b60a25',
  notes: '793c38d8-bd4d-4394-af34-a48782b7ed55',
  instructions: '505f8656-9293-47fe-9c21-1d1c541368a4',
  useLocaleQuantityFormatting: '9327682a-263d-48c4-a7bd-629d96d6599d',
}
